import React, { useState, useEffect } from 'react';
import {
  X, Loader2, ShieldCheck, AlertTriangle, ChevronDown, ChevronUp,
  Clock, History,
} from 'lucide-react';
import toast from '../../../utils/toast';
import Modal from '../../../components/UI/Modal';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import { talentAssessmentsAPI } from '../../../services/api';
import { formatDateTime } from '../../../utils/dateUtils';
import { getDepartmentHex } from '../../../config/departmentConfig';
import {
  TRAIT_ORDER,
  TRAIT_META,
  ASPECT_META,
  qualitativeBucket,
} from '../../../config/bigFiveMeta';
import OceanRadarChart from './OceanRadarChart';
import { OceanColorKey } from './aggregateShared';

/**
 * Individual result modal — Big Five.
 *
 * Modes:
 *   admin (default) — Talent Insights; red theme; T-score/raw + quality flags.
 *   team            — My Team (HOD / Deputy / RO); navy theme; employee vocabulary only.
 *
 * Two-step UX: justification gate → result reveal. Grace-window short-circuit
 * via `target._preloadedResult` (parent optimistic POST).
 *
 * `departmentAggregate` (team mode only, HOD/Deputy HOD): the viewer's own
 * department's live OCEAN benchmark, matching the shape returned by
 * `talentAssessmentsAPI.getTeamBigFiveDepartmentAggregate()` — i.e.
 * `{ department, respondent_count, meets_threshold, shortfall, k_threshold, traits? }`.
 * When supplied, the result step renders an "in context" radar overlaying
 * the individual against the department mean. Pass `null`/`undefined` for
 * admin mode and for RO viewers — the section is absent entirely.
 */

const JUSTIFICATIONS = [
  { id: 'talent_development', label: 'Talent development' },
  { id: 'coaching', label: 'Coaching' },
  { id: 'role_fit', label: 'Role-fit discussion' },
  { id: 'succession_planning', label: 'Succession planning' },
  { id: 'performance_review_support', label: 'Performance review support' },
  { id: 'employee_requested_discussion', label: 'Employee requested this discussion' },
  { id: 'other', label: 'Other (specify)' },
];

const ASSESSMENT_CONFIG = {
  'big-five': {
    label: 'Big Five Profile',
    fetchAdmin: (staffId, payload) =>
      talentAssessmentsAPI.adminGetBigFiveIndividualResult(staffId, payload),
    fetchTeam: (staffId, payload) =>
      talentAssessmentsAPI.getTeamBigFiveIndividualResult(staffId, payload),
  },
};


const IndividualResultModal = ({
  target,
  onClose,
  assessment = 'big-five',
  mode = 'admin',
  departmentAggregate = null,
}) => {
  const isTeam = mode === 'team';
  const theme = isTeam ? 'employee' : 'admin';
  const [step, setStep] = useState('gate'); // 'gate' | 'result'
  const [code, setCode] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedTrait, setExpandedTrait] = useState(null);

  // Reset modal state when target changes. If the parent supplied a
  // `_preloadedResult` (because the grace window was active and the
  // result was already fetched), open directly at the result step and
  // skip the gate entirely.
  useEffect(() => {
    if (target) {
      if (target._preloadedResult) {
        setStep('result');
        setResult(target._preloadedResult);
        setCode('');
        setText('');
      } else {
        setStep('gate');
        setCode('');
        setText('');
        setResult(null);
      }
      setExpandedTrait(null);
    }
  }, [target]);

  if (!target) return null;

  const config = ASSESSMENT_CONFIG[assessment] || ASSESSMENT_CONFIG['big-five'];
  const fetchResult = isTeam ? config.fetchTeam : config.fetchAdmin;
  const valid = code && (code !== 'other' || text.trim().length > 0);
  const focusRing = isTeam
    ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1'
    : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1';
  const primaryBtn = isTeam ? 'btn btn-primary' : 'btn btn-admin-primary';
  const outlineBtn = isTeam ? 'btn btn-outline' : 'btn btn-admin-outline';
  const closeBtn = isTeam ? 'modal-close' : 'modal-close-admin';
  const requiredMark = isTeam ? 'text-blue-800' : 'text-red-600';

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await fetchResult(target.staff_id, {
        justification_code: code,
        justification_text: text.trim(),
      });
      setResult(data);
      setStep('result');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not load this employee\'s result.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      theme={theme}
      className="max-w-3xl"
      customHeader={(
        <div className="modal-header">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wider text-gray-500">
              {config.label}
              {isTeam ? ' · career conversation' : ' · individual result'}
            </div>
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {target.display_name || target.staff_id}
              <span className="ml-2 text-xs text-gray-500 font-normal">
                {target.staff_id}
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={closeBtn}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      footer={(
        <>
          {step === 'gate' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className={outlineBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!valid || submitting}
                className={primaryBtn}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reveal result'}
              </button>
            </>
          )}
          {step === 'result' && (
            <button
              type="button"
              onClick={onClose}
              className={`${outlineBtn} w-full`}
            >
              Close
            </button>
          )}
        </>
      )}
    >
          {step === 'gate' && (
            <JustificationGate
              code={code}
              setCode={setCode}
              text={text}
              setText={setText}
              employee={target}
              assessmentLabel={config.label}
              isTeam={isTeam}
              requiredMark={requiredMark}
            />
          )}
          {step === 'result' && result && (
            <ResultView
              result={result}
              audit={result.audit}
              expandedTrait={expandedTrait}
              setExpandedTrait={setExpandedTrait}
              showTechnicalScores={!isTeam}
              showQualityFlags={!isTeam}
              focusRing={focusRing}
              departmentAggregate={isTeam ? departmentAggregate : null}
            />
          )}
    </Modal>
  );
};


const JustificationGate = ({
  code, setCode, text, setText, employee, assessmentLabel, isTeam, requiredMark,
}) => {
  const selectedBorder = isTeam
    ? 'border-blue-800 bg-blue-50'
    : 'border-red-500 bg-red-50';
  const radioClass = isTeam
    ? 'w-4 h-4 text-blue-800 border-gray-300 focus:ring-blue-500'
    : 'w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500';
  const textareaClass = isTeam ? 'textarea' : 'textarea-admin';

  return (
  <div className="space-y-5">
    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-900">
        Viewing this individual's {assessmentLabel} will be recorded with your name, the time, and the reason you provide. <strong>{employee.display_name || employee.staff_id}</strong> can see this entry on their own access log.
      </div>
    </div>

    <div>
      <label className="block text-sm font-semibold text-gray-900 mb-2">
        Reason for viewing <span className={requiredMark}>*</span>
      </label>
      <div className="space-y-2">
        {JUSTIFICATIONS.map((j) => (
          <label
            key={j.id}
            className={[
              'flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg border cursor-pointer transition',
              code === j.id
                ? selectedBorder
                : 'border-gray-200 hover:bg-gray-50',
            ].join(' ')}
          >
            <input
              type="radio"
              name="justification"
              value={j.id}
              checked={code === j.id}
              onChange={(e) => setCode(e.target.value)}
              className={radioClass}
            />
            <span className="text-sm text-gray-900">{j.label}</span>
          </label>
        ))}
      </div>
    </div>

    {code === 'other' && (
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-1">
          Please specify <span className={requiredMark}>*</span>
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder={`Describe why you need to view this individual's ${assessmentLabel}.`}
          className={textareaClass}
        />
        <div className="text-xs text-gray-500 mt-1">
          Be specific. The employee will see this exact text on their access log.
        </div>
      </div>
    )}

    <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 flex items-start gap-2">
      <ShieldCheck className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
      <div className="text-xs text-gray-600">
        Access is governed by company policy. Misuse may be subject to internal review.
        {isTeam
          ? ' Use this profile to prepare for career and coaching conversations — not for hiring or disciplinary decisions alone.'
          : ''}
      </div>
    </div>
  </div>
  );
};


const JUSTIFICATION_LABELS = {
  talent_development: 'Talent development',
  coaching: 'Coaching',
  role_fit: 'Role-fit discussion',
  succession_planning: 'Succession planning',
  performance_review_support: 'Performance review support',
  employee_requested_discussion: 'Employee requested this discussion',
  other: 'Other',
};


/**
 * Shared header for both result bodies: grace-window renewal banner,
 * submitted/scoring meta line, and the quality-flag caution banner.
 */
const ResultHeader = ({ result, audit, showQualityFlags = true }) => {
  const qualityFlags = result.result?.quality_flags || {};
  const submittedAt = result.session?.submitted_at
    ? formatDateTime(result.session.submitted_at)
    : null;

  const flagsTriggered = ['straight_lining', 'very_fast_completion', 'low_variance', 'many_revisions']
    .filter((k) => qualityFlags[k]);

  return (
    <>
      {audit?.is_grace_window_renewal && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 flex items-start gap-2.5">
          <History className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 text-xs text-blue-900 leading-relaxed">
            <span className="font-semibold">Reusing your recent justification. </span>
            You justified viewing this employee
            {' '}
            <strong>{formatDateTime(audit.original_accessed_at)}</strong>
            {' '}as
            {' '}
            <strong>
              {JUSTIFICATION_LABELS[audit.original_justification_code]
                || audit.original_justification_code}
            </strong>
            {audit.original_justification_text ? (
              <> &mdash; &ldquo;{audit.original_justification_text}&rdquo;</>
            ) : null}
            . Within {audit.grace_window_minutes} minutes we don't re-prompt.
          </div>
        </div>
      )}

      <div className="text-xs text-gray-600 flex items-center gap-2.5 flex-wrap">
        <span>Submitted {submittedAt} · Scoring v{result.session?.scoring_version}</span>
        {result.viewer_relationship_label && (
          <span className="inline-flex items-center rounded-full ring-1 ring-inset ring-blue-600/20 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-800">
            Viewing as {result.viewer_relationship_label}
          </span>
        )}
        {audit && !audit.is_grace_window_renewal && (
          <span
            className="inline-flex items-center gap-1 rounded-full ring-1 ring-inset ring-emerald-600/20 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
            title={`Audit entry written at ${formatDateTime(audit.original_accessed_at)}.`}
          >
            <Clock className="w-3 h-3" />
            Audit entry recorded
          </span>
        )}
      </div>

      {showQualityFlags && flagsTriggered.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
          <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Interpretation caution
          </div>
          <div className="mt-0.5 text-xs text-amber-900">
            This session triggered {flagsTriggered.length} quality flag{flagsTriggered.length === 1 ? '' : 's'}: <strong>{flagsTriggered.join(', ').replace(/_/g, ' ')}</strong>. Treat these scores as indicative rather than definitive.
          </div>
        </div>
      )}
    </>
  );
};


const EMPLOYEE_SERIES_COLOR = '#1d4ed8';

/** First given name only — keeps the anti-conformity framing line short and personal. */
const firstNameOf = (displayName) => (displayName || '').trim().split(/\s+/)[0] || 'this person';

/**
 * "In context" card — team mode only, HOD/Deputy HOD. Overlays the
 * individual's OCEAN shape against their department's live mean so the
 * viewer answers "how does this person sit within my team?" in one
 * glance, without a company-wide comparison diluting the read.
 *
 * Anti-conformity framing (locked, see plan): differences are what the
 * person *adds* to the department's mix, never a gap from a target —
 * no good/bad language, no "distance from average" phrasing.
 */
const DepartmentContextCard = ({ traitScores, departmentAggregate, displayName }) => {
  const { department, meets_threshold: meetsThreshold, respondent_count: respondentCount,
    shortfall, k_threshold: kThreshold, traits } = departmentAggregate;

  const individualValues = {};
  TRAIT_ORDER.forEach((code) => {
    const raw = traitScores[code]?.raw;
    if (raw != null) individualValues[code] = raw;
  });

  const series = [
    {
      id: 'individual',
      label: displayName || 'This profile',
      color: EMPLOYEE_SERIES_COLOR,
      values: individualValues,
    },
  ];

  if (meetsThreshold && traits) {
    const departmentValues = {};
    TRAIT_ORDER.forEach((code) => {
      const mean = traits[code]?.mean;
      if (mean != null) departmentValues[code] = mean;
    });
    series.push({
      id: 'department',
      label: `${department} average`,
      color: getDepartmentHex(department),
      dashed: true,
      values: departmentValues,
    });
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3.5 sm:p-4">
      <div className="text-sm font-semibold text-gray-900">
        In context: how this profile sits in {department}
      </div>

      <div className="mt-2">
        <OceanRadarChart series={series} size="card" showLegend={meetsThreshold} />
      </div>

      <OceanColorKey className="mt-3" />

      {meetsThreshold ? (
        <p className="mt-3 text-xs text-gray-600 leading-relaxed">
          Differences from the team average aren&apos;t gaps — they show what {firstNameOf(displayName)} adds to the team&apos;s mix. There are no good or bad profiles.
        </p>
      ) : (
        <p className="mt-3 text-xs text-gray-500 leading-relaxed">
          Department comparison appears once {kThreshold}+ colleagues have completed the profile ({respondentCount} so far{shortfall ? `, ${shortfall} more needed` : ''}).
        </p>
      )}
    </div>
  );
};

const ResultView = ({
  result,
  audit,
  expandedTrait,
  setExpandedTrait,
  showTechnicalScores = true,
  showQualityFlags = true,
  focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1',
  departmentAggregate = null,
}) => {
  const traitScores = result.result?.trait_scores || {};
  const facetScores = result.result?.facet_scores || {};

  return (
    <div className="space-y-3">
      <ResultHeader result={result} audit={audit} showQualityFlags={showQualityFlags} />

      {departmentAggregate && (
        <DepartmentContextCard
          traitScores={traitScores}
          departmentAggregate={departmentAggregate}
          displayName={result.employee?.display_name}
        />
      )}

      <div className="space-y-2">
        {TRAIT_ORDER.map((traitCode) => {
          const trait = traitScores[traitCode];
          if (!trait) return null;
          const meta = TRAIT_META[traitCode];
          const expanded = expandedTrait === traitCode;
          const pct = trait.percentile_public;
          const bucket = qualitativeBucket(pct);
          return (
            <div key={traitCode} className="rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedTrait(expanded ? null : traitCode)}
                className={`w-full min-h-[44px] text-left px-3 py-2.5 hover:bg-gray-50 transition ${focusRing}`}
                aria-expanded={expanded}
              >
                <div className="flex items-start gap-2.5">
                  <span className={`w-1.5 h-6 rounded-sm ${meta.bar} flex-shrink-0 mt-0.5`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {meta.name}
                        </div>
                        {bucket && (
                          <InfoBadge color={bucket.color} label={bucket.label} />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div
                          className={`text-sm font-bold ${meta.bar.replace('bg-', 'text-')} tabular-nums`}
                          title={`${pct != null ? pct.toFixed(1) : '—'}% — score lower than this employee.`}
                        >
                          {pct != null ? `${pct.toFixed(0)}%` : '—'}
                        </div>
                        {expanded
                          ? <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                          : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />}
                      </div>
                    </div>

                    <div className="mt-0.5 flex items-baseline gap-x-2 gap-y-0 flex-wrap text-[11px] leading-snug">
                      <span className="text-gray-600">{meta.caption}</span>
                      {showTechnicalScores && (
                        <>
                          <span className="text-gray-300 hidden sm:inline" aria-hidden>·</span>
                          <span className="text-gray-500 tabular-nums whitespace-nowrap">
                            <span title="Standardised score (T-score) — mean 50, SD 10.">
                              T <span className="font-semibold text-gray-700">{trait.tscore != null ? trait.tscore.toFixed(1) : '—'}</span>
                            </span>
                            <span className="text-gray-300 mx-1.5">·</span>
                            <span title="Sum of keyed item responses for this trait.">
                              Raw <span className="font-semibold text-gray-700">{trait.raw != null ? trait.raw : '—'}</span>
                            </span>
                            <span className="text-gray-300 mx-1.5">·</span>
                            <span title="Percentile against public norms (precise).">
                              Pct <span className="font-semibold text-gray-700">{pct != null ? `${pct.toFixed(1)}%` : '—'}</span>
                            </span>
                          </span>
                        </>
                      )}
                    </div>

                    <div className="mt-1.5 relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300 z-10" />
                      <div
                        className={`h-full ${meta.bar} transition-all duration-500`}
                        style={{ width: `${Math.max(0, Math.min(100, pct ?? 0))}%` }}
                      />
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-[10px] leading-tight text-gray-500">
                      <span className="truncate">{meta.low}</span>
                      <span className="truncate text-right">{meta.high}</span>
                    </div>
                  </div>
                </div>
              </button>
              {expanded && (
                <div className="px-3 pb-3 bg-gray-50 border-t border-gray-200">
                  <div className="mt-2.5 mb-1.5 text-xs text-gray-600">
                    The six aspects below break <strong>{meta.name}</strong> into the more specific tendencies that make it up.
                  </div>
                  <ul className="divide-y divide-gray-200">
                    {[1, 2, 3, 4, 5, 6].map((idx) => {
                      const code = `${traitCode}${idx}`;
                      const aspect = facetScores[code];
                      if (!aspect) return null;
                      const aspectMeta = ASPECT_META[code] || { name: code, blurb: '' };
                      const fpct = aspect.percentile_public;
                      return (
                        <li key={code} className="py-2 first:pt-0 last:pb-0">
                          <div className="flex items-baseline justify-between gap-3">
                            <div className="text-sm font-semibold text-gray-900 min-w-0">
                              {aspectMeta.name}
                            </div>
                            <span className={`text-sm font-bold ${meta.bar.replace('bg-', 'text-')} tabular-nums flex-shrink-0`}>
                              {fpct != null ? `${fpct.toFixed(0)}%` : '—'}
                            </span>
                          </div>
                          {aspectMeta.blurb && (
                            <p className="mt-0.5 text-xs text-gray-600 leading-snug">
                              {aspectMeta.blurb}
                            </p>
                          )}
                          <div className="mt-1.5 relative h-1.5 bg-white rounded-full overflow-hidden border border-gray-200">
                            <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300 z-10" />
                            <div
                              className={`h-full ${meta.bar} transition-all duration-500`}
                              style={{ width: `${Math.max(0, Math.min(100, fpct ?? 0))}%` }}
                            />
                          </div>
                          {showTechnicalScores && (
                            <div className="mt-1 text-[11px] text-gray-500 tabular-nums">
                              Raw <span className="font-semibold text-gray-700">{aspect.raw != null ? aspect.raw : '—'}/20</span>
                              <span className="text-gray-300 mx-1.5">·</span>
                              Pct <span className="font-semibold text-gray-700">{fpct != null ? `${fpct.toFixed(1)}%` : '—'}</span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IndividualResultModal;
